"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Lock, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { isFavoriteTool, toggleFavoriteTool } from "@/lib/favorite-tools";
import { lockedToolHref } from "@/lib/billing/planLabel";
import { studioLockCopy, type HubTool } from "./hubData";
import { HubBadge } from "./HubBadge";

export function ToolShowcaseCard({
  tool,
  locked = false,
  imageUrl = null,
  lockedHref,
}: {
  tool: HubTool;
  locked?: boolean;
  imageUrl?: string | null;
  /**
   * Override for the locked-card destination. Defaults to `lockedToolHref`
   * (the subscriber-hub behavior: /subscription with the upgrade
   * target params). A caller with more context about the viewer
   * — e.g. FreeHome, which knows every viewer is on the free plan and that
   * /subscription would just bounce them on to /#pricing — can pass a more
   * direct destination here instead.
   */
  lockedHref?: string;
}) {
  const Icon = tool.icon;
  const href = locked ? (lockedHref ?? lockedToolHref(tool.featureKey)) : tool.href;

  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    setFavorited(isFavoriteTool(tool.href));
    const onChange = () => setFavorited(isFavoriteTool(tool.href));
    window.addEventListener("workshop-favorites-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("workshop-favorites-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [tool.href]);

  return (
    <div className="group relative h-full">
      <Link
        href={href}
        aria-disabled={locked}
        className="flex h-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card/90 transition duration-200 hover:-translate-y-0.5 hover:border-accent/40"
      >
        <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-secondary/80 via-card/80 to-card">
          {imageUrl ? (
            <>
              <img
                src={imageUrl}
                alt=""
                aria-hidden
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card/80 to-transparent" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_40%,_var(--tw-gradient-stops))] from-accent/8 via-transparent to-transparent" />
              <div className="flex h-full items-center justify-center">
                <Icon className="h-20 w-20 text-foreground/[0.06]" />
              </div>
            </>
          )}
          {locked && (
            // Rests transparent so the screenshot underneath stays visible; on
            // hover or keyboard focus (the whole card is one <Link>, so
            // focusing it triggers group-focus-within same as tabbing to any
            // other card) it dims the image and surfaces the unlock CTA. A
            // touch device has no hover state, but the entire tile is already
            // the link to `href` and the "Upgrade to unlock" pill below stays
            // permanently visible in the card body — so the action never
            // depends on this overlay actually showing.
            <div className="absolute inset-0 grid place-items-center bg-background/80 opacity-0 backdrop-blur-[2px] transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
              <div className="flex flex-col items-center gap-1.5 px-3 text-center">
                <span className="grid h-9 w-9 place-items-center rounded-xl border border-border/70 bg-background/85 text-foreground/80">
                  <Lock className="h-4 w-4" />
                </span>
                <span className="text-xs font-semibold tracking-tight text-foreground">
                  {studioLockCopy(tool.featureKey)?.cta ?? "Upgrade to unlock"}
                </span>
                {(tool.lockedNote ?? studioLockCopy(tool.featureKey)?.note) && (
                  <span className="max-w-[15rem] text-[11px] leading-snug text-muted-foreground">
                    {tool.lockedNote ?? studioLockCopy(tool.featureKey)?.note}
                  </span>
                )}
                <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-[11px] font-semibold text-accent-foreground">
                  See plans
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-3 p-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold tracking-tight text-foreground">{tool.title}</h3>
            {locked ? (
              <LockedChip />
            ) : tool.badge ? (
              <HubBadge kind={tool.badge} />
            ) : null}
          </div>

          <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
            {tool.blurb}
          </p>

          {tool.bullets && tool.bullets.length > 0 && (
            <ul className="space-y-1">
              {tool.bullets.slice(0, 3).map((bullet) => (
                <li
                  key={bullet}
                  className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground"
                >
                  <CheckCircle2 className="mt-0.5 h-3 w-3 flex-none text-accent" />
                  {bullet}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-auto flex items-center gap-2 pt-1">
            <span className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-accent px-3 py-2 text-xs font-semibold text-accent-foreground transition group-hover:opacity-90">
              {locked ? (
                <>
                  <Lock className="h-3 w-3" />
                  {studioLockCopy(tool.featureKey)?.cta ?? "Upgrade to unlock"}
                </>
              ) : (
                <>
                  Open {tool.title}
                  <ArrowRight className="h-3 w-3" />
                </>
              )}
            </span>
          </div>
        </div>
      </Link>

      {!locked && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const next = toggleFavoriteTool(tool.href);
            setFavorited(next);
          }}
          aria-label={favorited ? `Remove ${tool.title} from favorites` : `Favorite ${tool.title}`}
          title={favorited ? "Unfavorite" : "Favorite"}
          className={cn(
            "absolute right-2.5 top-2.5 grid h-7 w-7 place-items-center rounded-md backdrop-blur-sm transition",
            favorited
              ? "bg-background/60 text-amber-400 hover:bg-background/80"
              : "bg-background/40 text-muted-foreground opacity-0 hover:bg-background/70 hover:text-foreground group-hover:opacity-100 focus:opacity-100",
          )}
        >
          <Star className={cn("h-3.5 w-3.5", favorited ? "fill-current" : "")} />
        </button>
      )}
    </div>
  );
}

function LockedChip() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-secondary/40 px-1.5 py-px font-mono text-[8.5px] uppercase tracking-[0.18em] text-muted-foreground">
      <Lock className="h-2.5 w-2.5" />
      Locked
    </span>
  );
}
